import React from 'react';
import PropTypes from 'prop-types';
import { pathOr, path } from 'ramda';
import { FormattedMessage } from 'react-intl';

import { Row, Column } from 'components/shared/Layout';
import AlertBox from 'components/ui/AlertBox';
import FareIndicator from 'components/ui/FareIndicator';

import JourneySearchTables from 'components/shared/JourneySearchTables';
import JourneySearchHeader from 'components/shared/JourneySearchHeader';

import { offersShape, legShape, fareShape, productShape, bookingShape } from 'constants/propTypes';

import { OUTBOUND, INBOUND } from 'constants/booking';
import { isSameTrain } from 'utils/isSameTrain';
import { getLegsAndConditions, isSameDayEurostarExchangeWithFlexibleConnection } from 'utils/summatedFares';
import { hasProductFamilyMismatch } from 'pages/JourneySearch/JourneySearch.utils';
import { isExistingTrain } from 'utils/isExistingTrain';
import { isJourneyTimeRestricted } from 'utils/isJourneyTimeRestricted';
import { formatProductFamilyName } from 'utils/functions';

import {
  getExistingTrains,
  getProductFamiliesBeingExchanged,
  isExchangingOFETrain,
  getTariffCodeBeingExchanged,
  getTariffCodeSelected
} from '../Exchange.utils';

import { isBookingMiceGroup } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';

import ExchangeConfirmSelection from './ExchangeConfirmSelection';
import { getAOPStatus, findClassOfServiceBeingExchanged } from './ExchangeResult.utils';
import ExchangeSummary from '../ExchangeSummary';
import { buildExchangeSummarySegments } from '../ExchangeSummary/ExchangeSummary.utils';
import { types } from 'types/passengers';
import { isPublicBPPremiumNCO } from 'utils/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { EVAC, UNASSIGNED } from 'constants/classOfService';
import { isSNCBUnconfirmedTimetableBookingExchangeEnabled } from 'utils/featureFlags';
import { hasSNCBUnconfirmedTimetableSearchResults } from 'utils/sncbUnconfirmedTimetable';

const ExchangeResults = ({
  status,
  outboundDate,
  inboundDate,
  outboundSelectedFare,
  inboundSelectedFare,
  isGroupBooking,
  reference,
  currency,
  searchData,
  handleSelectFare,
  outbound,
  inbound,
  outboundJourneyInfo,
  inboundJourneyInfo,
  overrideReason,
  isStationAgent,
  stationName,
  outboundTrainNumbers,
  inboundTrainNumbers,
  isMember,
  activePoints,
  hasSpecialLoyaltyTier,
  swapStations,
  shouldForceOutbound,
  shouldForceInbound,
  outboundExtras,
  inboundExtras,
  isMultiJourneyBooking,
  isMultiJourney,
  booking,
  outboundLegsBeingExchanged,
  inboundLegsBeingExchanged,
  isExchangingSinglegOfAOutboundSummated,
  isExchangingMultipleLegsOfAOutboundSummated,
  isExchangingSingleLegOfAInboundSummated,
  isExchangingMultipleLegsOfAInboundSummated,
  isExchangingASubsetOfPassengersOutbound,
  isExchangingASubsetOfPassengersInbound,
  isSummatedFaresSelectionEnabled,
  isExchangingOutboundDirectFare,
  isExchangingInboundDirectFare,
  isExchangingOutboundThroughFare,
  isExchangingInboundThroughFare,
  subscriptionStartDate,
  subscriptionEndDate,
  isThalysUser,
  bookingItemRefs,
  animalsPerTrain,
  animalsPerTrainLoading,
  assistancePerTrain,
  assistancePerTrainLoading,
  serviceDetailsData,
  serviceDetailsLoading
}) => {
  const isSubmitDisabled = () => {
    const isReturnExch = !!outboundDate && !!inboundDate;
    if (isReturnExch) {
      return !outboundSelectedFare || !inboundSelectedFare;
    }

    return !(!!outboundSelectedFare || !!inboundSelectedFare);
  };

  const isAllJourneysSelected = () => {
    const isReturnExch = !!outboundDate && !!inboundDate;

    if (isReturnExch) {
      return !!outboundSelectedFare && !!inboundSelectedFare;
    }

    if (outboundDate) {
      return !!outboundSelectedFare;
    }

    if (inboundDate) {
      return !!inboundSelectedFare;
    }

    return false;
  };

  const isMiceGroup = isBookingMiceGroup({ details: booking?.additionalDetails });
  const { passengers, productFamiliesWithFares, outbound: outboundSearch, inbound: inboundSearch } = searchData;
  const existingTrains = getExistingTrains({ outbound, inbound });
  const includesOFETrain = isExchangingOFETrain({ outbound, inbound });

  const requestedProductFamilies = getProductFamiliesBeingExchanged({
    outbound,
    inbound
  });

  const hasMismatch = hasProductFamilyMismatch(requestedProductFamilies, productFamiliesWithFares);
  const showSNCBUnconfirmedTimetableWarning =
    isSNCBUnconfirmedTimetableBookingExchangeEnabled() && hasSNCBUnconfirmedTimetableSearchResults(searchData);
  const uniqueFamilyGroupNames = [...new Set(productFamiliesWithFares.map((pf) => pf.familyGroupName))];
  const allProductFamiliesMessage = uniqueFamilyGroupNames.map((name) => formatProductFamilyName(name)).join(', ');
  const outboundOriginalClassOfService = outbound?.classOfService?.code;
  const inboundOriginalClassOfService = inbound?.classOfService?.code;
  const tariffCodeBeingExchangedOutbound = getTariffCodeBeingExchanged(outbound);
  const tariffCodeSelectedOutbound = getTariffCodeSelected(outboundSelectedFare);
  const tariffCodeBeingExchangedInbound = getTariffCodeBeingExchanged(inbound);
  const tariffCodeSelectedInbound = getTariffCodeSelected(inboundSelectedFare);

  const isExchangingOutboundSummatedfare = isExchangingSinglegOfAOutboundSummated || isExchangingMultipleLegsOfAOutboundSummated;

  const isExchangingInboundSummatedfare = isExchangingSingleLegOfAInboundSummated || isExchangingMultipleLegsOfAInboundSummated;

  const getOutboundSummatedFareExchangeCondition =
    isExchangingOutboundSummatedfare && getLegsAndConditions(booking, outboundLegsBeingExchanged, OUTBOUND, outboundDate);
  const getInboundSummatedFareExchangeCondition =
    isExchangingInboundSummatedfare && getLegsAndConditions(booking, inboundLegsBeingExchanged, INBOUND, inboundDate);

  const showShortConnectionTimeWarning =
    (isExchangingSinglegOfAOutboundSummated &&
      !isSameDayEurostarExchangeWithFlexibleConnection(booking?.outbound, outboundLegsBeingExchanged, outboundDate)) ||
    (isExchangingSingleLegOfAInboundSummated &&
      !isSameDayEurostarExchangeWithFlexibleConnection(booking?.inbound, inboundLegsBeingExchanged, inboundDate));

  const exchangeBookingItems = [
    ...(outboundLegsBeingExchanged?.map(({ products }) => products) ?? []),
    ...(inboundLegsBeingExchanged?.map(({ products }) => products) ?? [])
  ].flat();

  const { isAOPActivated, isMaintainPriceActivated } = getAOPStatus(exchangeBookingItems);

  const outboundSummaryTickets = buildExchangeSummarySegments({ segment: booking?.outbound, bookingItemRefs });
  const inboundSummaryTickets = buildExchangeSummarySegments({ segment: booking?.inbound, bookingItemRefs });
  const outboundOriginalClassesOfAllServices = findClassOfServiceBeingExchanged(booking?.outbound, bookingItemRefs);
  const inboundOriginalClassesOfAllServices = findClassOfServiceBeingExchanged(booking?.inbound, bookingItemRefs);

  const isExchangingSingleOutboundLegOfSNCBSummated =
    isExchangingSinglegOfAOutboundSummated &&
    booking?.outbound?.filter((o) => !o.cancelled)?.some((item) => item.serviceType?.code === serviceTypeCodes.SNCB);
  const isExchangingSingleInboundLegOfSNCBSummated =
    isExchangingSingleLegOfAInboundSummated &&
    booking?.inbound?.filter((o) => !o.cancelled)?.some((item) => item.serviceType?.code === serviceTypeCodes.SNCB);

  return (
    <Row>
      <h1>
        <FormattedMessage id="exchange.header" />
      </h1>

      {isMultiJourneyBooking && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING} dataCy="warning-multi-journeys-er-only">
            <FormattedMessage id="journeySearch.multiJourneyWarningER" />
          </AlertBox>
        </Row>
      )}

      {hasMismatch && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage
              id="journeySearch.multipleProductFamilies"
              values={{
                actualProductFamilies: allProductFamiliesMessage,
                indicator: <FareIndicator data-cy="fare-indicator" />
              }}
            />
          </AlertBox>
        </Row>
      )}

      {showSNCBUnconfirmedTimetableWarning && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING} dataCy="sncb-unconfirmed-timetable-warning">
            <FormattedMessage id="sncbUnconfirmedTimetable.searchWarning" />
          </AlertBox>
        </Row>
      )}

      {isPublicBPPremiumNCO({
        outbound: {
          outboundOriginalDate: outbound?.timing?.date,
          outboundOriginalClassOfService: outbound?.classOfService?.code,
          outboundDate
        },
        inbound: {
          inboundOriginalDate: inbound?.timing?.date,
          inboundOriginalClassOfService: inbound?.classOfService?.code,
          inboundDate
        },
        requestedProductFamilies
      }) && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage id="journeySearch.isPublicBPPremiumNCO" />
          </AlertBox>
        </Row>
      )}

      {showShortConnectionTimeWarning && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING} dataCy="warning-short-connection-time">
            <FormattedMessage id="exchange.shortConnectionTimeWarning" />
          </AlertBox>
        </Row>
      )}

      <JourneySearchHeader
        booking={booking}
        inbound={inboundJourneyInfo}
        outbound={outboundJourneyInfo}
        swapStations={swapStations && !isAOPActivated}
        subscriptionStartDate={subscriptionStartDate}
        subscriptionEndDate={subscriptionEndDate}
        isExchangingSingleOutboundLegOfSNCBSummated={isExchangingSingleOutboundLegOfSNCBSummated}
        isExchangingSingleInboundLegOfSNCBSummated={isExchangingSingleInboundLegOfSNCBSummated}
      />

      <JourneySearchTables
        booking={booking}
        shouldUseSelection
        showFareDifference
        outboundDate={outboundDate}
        inboundDate={inboundDate}
        outboundInfo={outboundJourneyInfo}
        inboundInfo={inboundJourneyInfo}
        existingOutbound={outbound}
        existingInbound={inbound}
        outboundOrigin={outboundJourneyInfo && outboundJourneyInfo.originUIC}
        isExchangingASubsetOfPassengersOutbound={isExchangingASubsetOfPassengersOutbound}
        isExchangingASubsetOfPassengersInbound={isExchangingASubsetOfPassengersInbound}
        outboundDestination={outboundJourneyInfo && outboundJourneyInfo.destinationUIC}
        inboundOrigin={inboundJourneyInfo && inboundJourneyInfo.originUIC}
        inboundDestination={inboundJourneyInfo && inboundJourneyInfo.destinationUIC}
        originalOutboundTotal={path(['outbound', 'totalPriceOfOriginalExchangeItems'], searchData)}
        originalInboundTotal={path(['inbound', 'totalPriceOfOriginalExchangeItems'], searchData)}
        outboundJourneys={pathOr([], ['outbound', 'journeys'], searchData).map((j) => ({
          ...j,
          isExistingTrain: isExistingTrain(existingTrains.outbound, outboundDate, outboundTrainNumbers),
          isSameTrain: isSameTrain(existingTrains.outbound, outboundDate, outboundTrainNumbers),
          isTimeRestricted: isJourneyTimeRestricted({
            outboundDate,
            outboundArrives: j.timing.arrives,
            inboundDate: inboundDate || booking.inbound?.filter((i) => !i.cancelled)?.[0]?.timing?.date,
            inboundDeparts:
              (inboundSelectedFare && inboundSelectedFare.legs[0].timing.departs) ||
              (booking.inbound?.filter((i) => !i.cancelled)?.length === 1 &&
                booking.inbound?.filter((i) => !i.cancelled)?.[0]?.timing?.departs)
          })
        }))}
        inboundJourneys={pathOr([], ['inbound', 'journeys'], searchData).map((j) => ({
          ...j,
          isExistingTrain: isExistingTrain(existingTrains.inbound, inboundDate, inboundTrainNumbers),
          isSameTrain: isSameTrain(existingTrains.inbound, inboundDate, inboundTrainNumbers),
          isTimeRestricted: isJourneyTimeRestricted({
            outboundDate: outboundDate || booking.outbound?.filter((i) => !i.cancelled)?.[0]?.timing?.date,
            outboundArrives:
              (outboundSelectedFare && outboundSelectedFare.legs[outboundSelectedFare.legs.length - 1].timing.arrives) ||
              (booking.outbound?.filter((i) => !i.cancelled)?.length === 1 &&
                booking.outbound?.filter((i) => !i.cancelled)?.[0]?.timing?.arrives),
            inboundDate,
            inboundDeparts: j.timing.departs
          })
        }))}
        selectedOutbound={outboundDate && outboundSelectedFare}
        selectedInbound={inboundDate && inboundSelectedFare}
        selectFare={handleSelectFare}
        currency={currency}
        isGroupBooking={isGroupBooking}
        existingTrains={existingTrains}
        requestedProductFamilies={requestedProductFamilies}
        isWheelchairSearch={path(['outbound', 'isWheelchair'], searchData) || path(['inbound', 'isWheelchair'], searchData)}
        isEvacSearch={outboundOriginalClassOfService === EVAC || inboundOriginalClassOfService === EVAC}
        isUnassignedSearch={outboundOriginalClassOfService === UNASSIGNED || inboundOriginalClassOfService === UNASSIGNED}
        isExchange
        isMember={isMember}
        shouldForceOutbound={shouldForceOutbound}
        shouldForceInbound={shouldForceInbound}
        passengers={passengers}
        isMultiJourneyBooking={isMultiJourneyBooking}
        getOutboundSummatedFareExchangeCondition={getOutboundSummatedFareExchangeCondition}
        getInboundSummatedFareExchangeCondition={getInboundSummatedFareExchangeCondition}
        isSummatedFaresSelectionEnabled={isSummatedFaresSelectionEnabled}
        isHomogeneous={booking?.isHomogeneous}
        isExchangingMultipleLegsOfAOutboundSummated={isExchangingMultipleLegsOfAOutboundSummated}
        isExchangingMultipleLegsOfAInboundSummated={isExchangingMultipleLegsOfAInboundSummated}
        isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
        isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
        outboundLegsBeingExchanged={outboundLegsBeingExchanged}
        inboundLegsBeingExchanged={inboundLegsBeingExchanged}
        isExchangingOutboundDirectFare={isExchangingOutboundDirectFare}
        isExchangingInboundDirectFare={isExchangingInboundDirectFare}
        isExchangingOutboundThroughFare={isExchangingOutboundThroughFare}
        isExchangingInboundThroughFare={isExchangingInboundThroughFare}
        shouldOverridePrice={isAOPActivated && isMaintainPriceActivated}
        outboundClassOfServiceToExchange={outboundOriginalClassOfService}
        inboundClassOfServiceToExchange={inboundOriginalClassOfService}
        animalsPerTrain={animalsPerTrain}
        animalsPerTrainLoading={animalsPerTrainLoading}
        assistancePerTrain={assistancePerTrain}
        assistancePerTrainLoading={assistancePerTrainLoading}
        serviceDetailsData={serviceDetailsData}
        serviceDetailsLoading={serviceDetailsLoading}
        dogOnlySearch={searchData.passengers?.every(({ type }) => type === types.DOG)}
        outboundClassesOfService={outboundSearch?.classesOfService}
        inboundClassesOfService={inboundSearch?.classesOfService}
        outboundExtras={outboundExtras}
        inboundExtras={inboundExtras}
      />

      <Column span={3}>
        <ExchangeConfirmSelection
          status={status}
          data={{
            outboundSelectedFare,
            inboundSelectedFare,
            reference,
            currency,
            existingTrains,
            productFamiliesWithFares,
            isMember,
            activePoints,
            tariffCodeBeingExchangedOutbound,
            tariffCodeSelectedOutbound,
            tariffCodeBeingExchangedInbound,
            tariffCodeSelectedInbound,
            outboundExtras,
            inboundExtras,
            isAOPActivated,
            isMaintainPriceActivated
          }}
          overrideReason={overrideReason}
          disabled={isSubmitDisabled()}
          isStationAgent={isStationAgent}
          stationName={stationName}
          isExchangingOFETrain={includesOFETrain}
          outboundOriginalClassOfService={outboundOriginalClassOfService}
          inboundOriginalClassOfService={inboundOriginalClassOfService}
          swapStations={swapStations && !isAOPActivated}
          isMultiJourney={isMultiJourney}
          isSummatedFaresSelectionEnabled={isSummatedFaresSelectionEnabled}
          isExchangingMultipleLegsOfAOutboundSummated={isExchangingMultipleLegsOfAOutboundSummated}
          isExchangingMultipleLegsOfAInboundSummated={isExchangingMultipleLegsOfAInboundSummated}
          isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
          isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
          isExchangingOutboundDirectFare={isExchangingOutboundDirectFare}
          isExchangingInboundDirectFare={isExchangingInboundDirectFare}
          isExchangingOutboundThroughFare={isExchangingOutboundThroughFare}
          isExchangingInboundThroughFare={isExchangingInboundThroughFare}
          wasPurchasedWithSubscription={booking?.wasPurchasedWithSubscription}
          isThalysUser={isThalysUser}
          hasSpecialLoyaltyTier={hasSpecialLoyaltyTier}
          outboundOriginalClassesOfAllServices={outboundOriginalClassesOfAllServices}
          inboundOriginalClassesOfAllServices={inboundOriginalClassesOfAllServices}
          outboundLegsBeingExchanged={outboundLegsBeingExchanged}
          inboundLegsBeingExchanged={inboundLegsBeingExchanged}
          isPackageBooking={booking?.isPackage}
        />
      </Column>

      <ExchangeSummary
        outboundTickets={outboundSummaryTickets}
        inboundTickets={inboundSummaryTickets}
        isMiceGroup={isMiceGroup}
        isAllJourneysSelected={isAllJourneysSelected()}
      />
    </Row>
  );
};

ExchangeResults.propTypes = {
  overrideReason: PropTypes.string,
  status: PropTypes.string,
  outbound: legShape,
  inbound: legShape,
  outboundSelectedFare: fareShape,
  inboundSelectedFare: fareShape,
  isGroupBooking: PropTypes.bool,
  reference: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  searchData: offersShape,
  isMember: PropTypes.bool,
  activePoints: PropTypes.number,
  handleSelectFare: PropTypes.func.isRequired,
  inboundJourneyInfo: PropTypes.shape({
    origin: PropTypes.string,
    originUIC: PropTypes.string,
    destination: PropTypes.string,
    destinationUIC: PropTypes.string,
    date: PropTypes.string
  }),
  outboundJourneyInfo: PropTypes.shape({
    origin: PropTypes.string,
    originUIC: PropTypes.string,
    destination: PropTypes.string,
    destinationUIC: PropTypes.string,
    date: PropTypes.string
  }),
  outboundDate: PropTypes.string,
  inboundDate: PropTypes.string,
  isStationAgent: PropTypes.bool,
  outboundTrainNumbers: PropTypes.arrayOf(PropTypes.string),
  inboundTrainNumbers: PropTypes.arrayOf(PropTypes.string),
  swapStations: PropTypes.bool,
  stationName: PropTypes.string,
  shouldForceOutbound: PropTypes.bool,
  shouldForceInbound: PropTypes.bool,
  outboundExtras: PropTypes.arrayOf(productShape),
  inboundExtras: PropTypes.arrayOf(productShape),
  isMultiJourneyBooking: PropTypes.bool,
  isMultiJourney: PropTypes.bool,
  booking: bookingShape,
  outboundLegsBeingExchanged: PropTypes.arrayOf(legShape),
  inboundLegsBeingExchanged: PropTypes.arrayOf(legShape),
  isExchangingSinglegOfAOutboundSummated: PropTypes.bool,
  isExchangingMultipleLegsOfAOutboundSummated: PropTypes.bool,
  isExchangingSingleLegOfAInboundSummated: PropTypes.bool,
  isExchangingMultipleLegsOfAInboundSummated: PropTypes.bool,
  isExchangingASubsetOfPassengersOutbound: PropTypes.bool,
  isExchangingASubsetOfPassengersInbound: PropTypes.bool,
  isSummatedFaresSelectionEnabled: PropTypes.bool,
  isExchangingOutboundDirectFare: PropTypes.bool,
  isExchangingInboundDirectFare: PropTypes.bool,
  isExchangingOutboundThroughFare: PropTypes.bool,
  isExchangingInboundThroughFare: PropTypes.bool,
  subscriptionStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  subscriptionEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isThalysUser: PropTypes.bool,
  hasSpecialLoyaltyTier: PropTypes.bool,
  bookingItemRefs: PropTypes.arrayOf(PropTypes.string),
  animalsPerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  animalsPerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  assistancePerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  assistancePerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  serviceDetailsData: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  serviceDetailsLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  })
};

ExchangeResults.defaultProps = {
  outboundTrainNumbers: [],
  inboundTrainNumbers: []
};

export default ExchangeResults;
